import {
  BLACK_COLOR,
  CornerType,
  GALVA_COLOR,
  INOX_COLOR,
  Position,
  WHITE_COLOR,
} from '../constants';
import { Color } from '../entities/Color';
import { ComponentKewlox } from './ComponentKewlox';
import { CornersEntity, PropsComponent, SearchInfo } from '../Data';
import { getColors } from '../utils/dataStorage';

import piecesProperties from '../tests/fixtures/piecesProperties.json';
import { CustomPiece } from './CustomPiece';

export function getOldReferenceColor(color: Color): string {
  switch (color.reference) {
    case INOX_COLOR:
      return 'I';
    case GALVA_COLOR:
      return 'G';
    case WHITE_COLOR:
      return 'B';
    case BLACK_COLOR:
      return 'N';
  }
  return '';
}

function getCornerOldNameData(color: Color, type: CornerType) {
  const base = getOldReferenceColor(color);

  if (type === CornerType.Normal) return base;

  if (type === CornerType.Hook) return `${base}:r`;

  if (type === CornerType.Front) return `${base}:a`;

  throw new Error('Corner type unexpected');
}

function getCornerPriceData(color: Color, type: CornerType): PropsComponent {
  const oldRef = getCornerOldNameData(color, type);
  const properties = piecesProperties.corners.find(
    (entry: any) => entry.type === oldRef,
  );
  if (!properties) {
    throw new Error("Can't find the properties for corner piece");
  }
  return properties;
}

export class Corner extends ComponentKewlox {
  readonly color: Color;
  readonly type: CornerType;
  readonly positionHole?: Position;
  static _lookup = ['height'];

  constructor(
    id: number,
    name: string,
    reference: string,
    price: number,
    weigth: number,
    forSale: boolean,
    visible: boolean,
    height: number,
    color: Color,
    type: CornerType = CornerType.Normal,
    positionHole?: Position,
  ) {
    super(id, name, reference, price, weigth, forSale, visible, height);
    this.color = color;
    this.type = type;
    if (positionHole) this.positionHole = positionHole;
    else this.positionHole = undefined;
  }

  get typeComponent() {
    return 'Corner';
  }

  static fromJSON(json: CornersEntity): Corner {
    let objColor: Color;
    if (typeof json.color === 'number') {
      const colors = getColors();
      objColor = colors[0];
      for (const colorItem of colors) {
        if (colorItem.id === json.color) {
          objColor = colorItem;
        }
      }
    } else {
      const c = Object.create(Color.prototype);
      objColor = Object.assign(c, this, json.color);
    }

    const o = Object.create(Corner.prototype);
    return Object.assign(o, this, json, {
      color: objColor,
    });
  }

  static reviver(key: string, value: any): any {
    return key === '' ? Corner.fromJSON(value) : value;
  }

  compareTo(otherComponent: ComponentKewlox): number {
    return this.height - otherComponent.height;
  }

  includeInto(otherComponent: ComponentKewlox): boolean {
    return this.height - otherComponent.height >= 0;
  }

  static getSearchInfos(
    height: number,
    color: Color,
    type: CornerType,
    positionHole?: Position,
  ): SearchInfo {
    if (positionHole) {
      return {
        lookup: Corner._lookup,
        key: { height, color: color.id, type, positionHole },
      };
    } else {
      return {
        lookup: Corner._lookup,
        key: { height, color: color.id, type },
      };
    }
  }

  getKey(): string {
    return Corner.generateKey(
      this.height,
      this.color,
      this.type,
      this.positionHole,
    );
  }

  static generateKey(
    height: number,
    color: Color,
    type: CornerType,
    positionHole?: Position,
  ) {
    let searchInfos = undefined;
    if (positionHole)
      searchInfos = Corner.getSearchInfos(height, color, type, positionHole);
    else {
      searchInfos = Corner.getSearchInfos(height, color, type);
    }

    return JSON.stringify(searchInfos.key);
  }

  public computeWeight(customPiece: CustomPiece<ComponentKewlox>): number {
    let weightMM = 0.5;
    switch (customPiece.color.reference) {
      case INOX_COLOR:
        weightMM = 0.432;
        break;
      case GALVA_COLOR:
        weightMM = 0.549;
        break;
      case WHITE_COLOR:
      case BLACK_COLOR:
        weightMM = 0.46;
        break;
    }
    return Math.round(weightMM * customPiece.height * 100) / 100 / 1000;
  }

  public computePrice(customCorner: CustomPiece<ComponentKewlox>): number {
    /**
     * This formula came from the old Kewapp and use data (main, coef, mat) from the old kewapp database
     *
     */
    const base = getOldReferenceColor(this.color);
    const props: PropsComponent | undefined = piecesProperties.corners.find(
      (entry: any) => entry.type === base,
    );
    if (!props) {
      throw new Error("Can't find the properties for corner piece");
    }

    const k1 = props.k1 ?? 0;
    const k2 = props.k2 ?? 0;
    const k3 = props.k3 ?? 0;
    const k4 =
      props.k4 &&
      (this.type === CornerType.Front || this.type === CornerType.Hook)
        ? props.k4
        : 0;
    const k5 = props.k5 && this.type === CornerType.Hook ? props.k5 : 0;

    const height = customCorner.height ?? this.height;
    const heightMicr = height / 1000;

    let price = (heightMicr * k1 + k2 + k4 + k5) * k3;

    //price *= propsGen.coef;
    price = Math.round(price * 100) / 100;

    return price;
  }
}
